import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  HStack,
  CircularProgress,
  ButtonGroup,
  IconButton,
  Flex,
  useToast,
} from '@chakra-ui/react';
import {
  RiBookmark3Line,
  RiDeleteBinLine,
  RiEyeLine,
  RiStarLine,
} from 'react-icons/ri';
import { LinkButton } from '../../../../../../../../../../../../../../../components/LinkButton';
import {
  IActivityItemBase,
  IActivityScheduleBase,
} from '../../../../../../../../../../../../../../../models/activities';
import {
  IBookingQueue,
  IDetailedBookingQueueActivityScheduleItem,
  listBookingQueuesService,
} from '../../../../../../../../../../../../../../../services/BookingQueues/ListBookingQueuesService';
import { updateBookingQueuesPriorityService } from '../../../../../../../../../../../../../../../services/BookingQueues/UpdateBookingQueuesPriorityService';
import {
  maskDateTime,
  maskMoney,
  maskShortDate,
} from '../../../../../../../../../../../../../../../utils/formatters/handleMask';
import { translateError } from '../../../../../../../../../../../../../../../utils/errors';
import { HandleBookingQueueModal } from '../../../../../../../../../../../../../../../components/HandleBookingQueueModal';
import {
  IDetailedActivity,
  IDetailedActivitySpot,
} from '../../../../../../../../../../../../../../../services/Activities/ShowActivitiesService';
import { useAuth } from '../../../../../../../../../../../../../../../hooks/auth';
import { ConfirmationModal } from '../../../../../../../../../../../../../../../components/ConfirmationModal';
import { deleteBookingQueuesService } from '../../../../../../../../../../../../../../../services/BookingQueues/DeleteBookingQueuesService';

interface IBookingQueueTableItemActivitySchedule extends IActivityScheduleBase {
  formattedEndTime: string;
  formattedStartTime: string;
}

interface IBookingTableScheduleItem
  extends IDetailedBookingQueueActivityScheduleItem {
  formattedPrice: string;
}

type IBookingQueueTableItemSpot = IDetailedActivitySpot;

interface IBookingQueueTableItemActivity extends IDetailedActivity {
  formattedCreatedAt: string;
  spot: IBookingQueueTableItemSpot;
}

export interface IBookingQueueTableItem extends IBookingQueue {
  activitySchedule: IBookingQueueTableItemActivitySchedule;
  formattedBookedDate: string;
  formattedCreatedAt: string;
  scheduleItems: IBookingTableScheduleItem[];
  items: IActivityItemBase[];
  activity: IBookingQueueTableItemActivity;
}

interface IBookingQueueTableProps extends BoxProps {
  activityScheduleId: string;
  bookedDate: Date;
}

export const BookingQueueTable = ({
  activityScheduleId,
  bookedDate,
  ...rest
}: IBookingQueueTableProps): JSX.Element => {
  const toast = useToast();

  const {
    assignment: authenticatedAssignment,
    venture: authenticatedVenture,
    experienceLevelsTitleMap,
  } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [isBookingRegisterModalVisible, setIsBookingRegisterModalVisible] =
    useState(false);
  const [bookingQueues, setBookingQueues] = useState<IBookingQueueTableItem[]>(
    [],
  );
  const [selectedBookingQueue, setSelectedBookingQueue] =
    useState<IBookingQueueTableItem>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const toggleDeleteConfirmationModal = useCallback(
    (bookingQueue?: IBookingQueueTableItem): void => {
      setSelectedBookingQueue(bookingQueue);

      setIsDeleteConfirmationModalVisible((prevState) => !prevState);
    },
    [],
  );

  const loadBookingQueues = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const bookingQueuesResult = await listBookingQueuesService({
        activityScheduleId,
        bookedDate: bookedDate.toLocaleDateString('fr-CA'),
        showQueuedBookingsOnly: true,
      });

      const parsedBookingQueues = bookingQueuesResult.map((bookingQueue) => {
        const [bookedYear, bookedMonth, bookedDay] =
          bookingQueue.bookedDate.split('-');

        return {
          ...bookingQueue,
          formattedBookedDate: `${bookedDay}/${bookedMonth}/${bookedYear}`,
          formattedCreatedAt: maskDateTime(bookingQueue.createdAt),
          activitySchedule: {
            ...bookingQueue.activitySchedule,
            formattedEndTime: bookingQueue.activitySchedule.endTime.slice(0, 5),
            formattedStartTime: bookingQueue.activitySchedule.startTime.slice(
              0,
              5,
            ),
          },
          scheduleItems: bookingQueue.scheduleItems.map((scheduleItem) => ({
            ...scheduleItem,
            formattedPrice: scheduleItem.price
              ? maskMoney(scheduleItem.price)
              : '-',
          })),
          activity: {
            ...bookingQueue.activity,
            formattedCreatedAt: maskShortDate(bookingQueue.activity.createdAt),
          },
          items: bookingQueue.items ?? [],
        };
      });

      setBookingQueues(parsedBookingQueues);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao carregar dados',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao carregar os dados da fila de espera, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [activityScheduleId, bookedDate, toast]);

  const handlePriority = async (bookingQueueId: string): Promise<void> => {
    try {
      await updateBookingQueuesPriorityService({
        bookingQueueId,
      });

      loadBookingQueues();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao carregar dados',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao carregar os dados da fila de espera, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  const handleDispatchQueue = (bookingQueue: IBookingQueueTableItem): void => {
    setSelectedBookingQueue(bookingQueue);
    setIsBookingRegisterModalVisible(true);
  };

  useEffect(() => {
    loadBookingQueues();
  }, [loadBookingQueues, activityScheduleId, bookedDate]);

  const handleDeleteBookingQueue = useCallback(
    async (bookingQueue: IBookingQueueTableItem) => {
      try {
        await deleteBookingQueuesService(bookingQueue.id);

        toast({
          title: 'Cancelada com sucesso',
          description: 'A reserva foi cancelada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        setBookingQueues((prevState) =>
          prevState.filter((bq) => bq.id !== bookingQueue.id),
        );

        toggleDeleteConfirmationModal(undefined);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao excluir',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao excluir a reserva na fila, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast, toggleDeleteConfirmationModal],
  );

  return (
    <Box {...rest}>
      {!!selectedBookingQueue && !!isBookingRegisterModalVisible && (
        <HandleBookingQueueModal
          activity={selectedBookingQueue.activity}
          bookingQueue={selectedBookingQueue}
          isOpen={isBookingRegisterModalVisible}
          isBookSubmiting
          onClose={() => {
            setSelectedBookingQueue(undefined);
            setIsBookingRegisterModalVisible(false);
          }}
        />
      )}

      {!!selectedBookingQueue && !!isDeleteConfirmationModalVisible && (
        <ConfirmationModal
          isOpen={isDeleteConfirmationModalVisible}
          onClose={() => toggleDeleteConfirmationModal(undefined)}
          onConfirm={() => handleDeleteBookingQueue(selectedBookingQueue)}
          title="Confirmar exclusão"
          message="Deseja realmente excluir?"
        />
      )}

      {bookingQueues.length ? (
        <Table colorScheme="blue" mt="4">
          <Thead>
            <Tr>
              <Th>Membro</Th>
              <Th>Experiência do usuário</Th>
              <Th textAlign="center">Horário reservado</Th>
              <Th textAlign="center">Reservado em</Th>
              <Th textAlign="right" w="8" />
            </Tr>
          </Thead>

          <Tbody>
            {bookingQueues.map((bookingQueue) => {
              const userOnboard = bookingQueue.user.onboards.find(
                (onboard) => onboard.ventureId === authenticatedVenture?.id,
              );

              return (
                <Tr key={bookingQueue.id}>
                  <Td>
                    <Text fontWeight="bold">{bookingQueue.user.name}</Text>

                    <Text textColor="gray.400">
                      {bookingQueue.user.userAssignments[0]?.featureGroup.name}
                    </Text>

                    <Text textColor="gray.400" fontSize="sm">
                      {bookingQueue.user.userAssignments[0]?.host?.name}
                    </Text>
                  </Td>

                  <Td>
                    {userOnboard?.onboardType === 'COMPLETED'
                      ? experienceLevelsTitleMap.get(userOnboard.experience)
                      : 'Não avaliado'}
                  </Td>

                  <Td textAlign="center">
                    <Box>
                      <Text>{bookingQueue.formattedBookedDate}</Text>
                      {bookingQueue.activitySchedule ? (
                        <Text>{`${bookingQueue.activitySchedule.formattedStartTime} - ${bookingQueue.activitySchedule.formattedEndTime}`}</Text>
                      ) : (
                        <Text color="red.500">Sem horário</Text>
                      )}
                    </Box>
                  </Td>

                  <Td textAlign="center">
                    <Text>{bookingQueue.formattedCreatedAt.split('-')[0]}</Text>
                    <Text>{bookingQueue.formattedCreatedAt.split('-')[1]}</Text>
                  </Td>

                  <Td textAlign="right">
                    <ButtonGroup>
                      {authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_FULL_ACCESS',
                            'BOOKING_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="Reservar"
                          size="sm"
                          colorScheme="green"
                          icon={<Icon as={RiBookmark3Line} fontSize="18" />}
                          onClick={() => {
                            handleDispatchQueue(bookingQueue);
                          }}
                        />
                      )}

                      {authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_QUEUE_FULL_ACCESS',
                            'BOOKING_QUEUE_PRIORITY_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="Priority"
                          size="sm"
                          icon={<Icon as={RiStarLine} fontSize="18" />}
                          fontSize="16"
                          colorScheme={
                            !bookingQueue.priorizedAt ? 'blackAlpha' : 'yellow'
                          }
                          onClick={() => handlePriority(bookingQueue.id)}
                        />
                      )}

                      <LinkButton
                        px={0}
                        to={{
                          pathname: '/booking-queues/details',
                          state: {
                            bookingQueueId: bookingQueue.id,
                          },
                        }}
                      >
                        <Icon as={RiEyeLine} fontSize="16" />
                      </LinkButton>

                      {authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_FULL_ACCESS',
                            'BOOKING_DELETE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="delete-booking"
                          size="sm"
                          icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                          colorScheme="red"
                          onClick={() =>
                            toggleDeleteConfirmationModal(bookingQueue)
                          }
                        />
                      )}
                    </ButtonGroup>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Flex w="full" h="24" justify="center" align="center">
          {loading ? (
            <HStack spacing="4">
              <CircularProgress isIndeterminate color="teal" size="2rem" />
              <Text color="gray.600">Carregando...</Text>
            </HStack>
          ) : (
            <Text color="gray.600">Nenhuma reserva na fila para esse dia</Text>
          )}
        </Flex>
      )}
    </Box>
  );
};
